import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogIn, LogOut, User, AlertCircle, Mail, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop`, withCredentials: true });

export default function ShopAccount() {
  const { slug } = useParams();
  const { t } = useI18n();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(true);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [working, setWorking] = useState(false);

  const loadMe = useCallback(async () => {
    try { const { data } = await shopApi.get(`/${slug}/customer/me`); setMe(data); }
    catch { setMe(null); }
    finally { setBusy(false); }
  }, [slug]);
  useEffect(() => { loadMe(); }, [loadMe]);

  const errMsg = (d) => d === "too_many_attempts" ? t("shop.storefront.too_many")
    : d === "b2b_disabled" ? t("shop.storefront.b2b_disabled") : t("shop.storefront.login_failed");

  const login = async () => {
    setError(""); setWorking(true);
    try { await shopApi.post(`/${slug}/customer/login`, { email, password: pw }); await loadMe(); }
    catch (e) { setError(errMsg(e?.response?.data?.detail)); }
    finally { setWorking(false); }
  };
  const logout = async () => { setWorking(true); try { await shopApi.post(`/${slug}/customer/logout`, {}); } catch {} setMe(null); setWorking(false); };
  const requestReset = async () => {
    setError(""); setWorking(true);
    try { await shopApi.post(`/${slug}/customer/request-reset`, { email }); setResetSent(true); } catch {}
    finally { setWorking(false); }
  };

  if (busy) return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4" data-testid="sf-account">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2"><User className="h-6 w-6 text-slate-700" /><h1 className="text-lg font-bold text-slate-900">{t("shop.storefront.account")}</h1></div>

        {me ? (
          <div data-testid="sf-account-signedin">
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <CheckCircle2 className="h-5 w-5" /><span>{t("shop.storefront.signed_in_as")}: <b data-testid="sf-account-name">{me.name}</b></span>
            </div>
            <button onClick={logout} disabled={working} data-testid="sf-account-logout"
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-slate-300 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">
              <LogOut className="h-4 w-4" />{t("shop.storefront.sign_out")}
            </button>
          </div>
        ) : (
          <div data-testid="sf-account-signin">
            <p className="mb-3 text-xs text-slate-500">{t("shop.storefront.help_account")}</p>
            {error && <div role="alert" data-testid="sf-account-error" className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertCircle className="h-4 w-4" />{error}</div>}
            {resetSent && <div data-testid="sf-account-reset-sent" className="mb-3 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700"><Mail className="h-4 w-4" />{t("shop.storefront.reset_sent")}</div>}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("shop.storefront.b2b_email")} data-testid="sf-account-email"
              className="mb-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("shop.storefront.b2b_password")} data-testid="sf-account-password"
              className="mb-3 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" onKeyDown={(e) => e.key === "Enter" && login()} />
            <button onClick={login} disabled={working || !email || !pw} data-testid="sf-account-login"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 font-semibold text-white disabled:opacity-50">
              {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <><LogIn className="h-4 w-4" />{t("shop.storefront.sign_in")}</>}
            </button>
            <button onClick={requestReset} disabled={working || !email} data-testid="sf-account-forgot"
              className="mt-2 w-full text-center text-xs font-medium text-blue-600 disabled:opacity-50">{t("shop.storefront.forgot")}</button>
          </div>
        )}
        <Link to={`/shop/${slug}`} className="mt-4 block text-center text-xs text-slate-400 hover:text-slate-600">{t("shop.storefront.back")}</Link>
      </div>
    </div>
  );
}
